import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import LoadingState from "@/components/base/LoadingState";
import EpisodeCard from "@/components/feature/EpisodeCard";
import { useSeo } from "@/hooks/useSeo";
import { formatDate, getHotEpisodes, searchContent, type SearchResult } from "@/lib/content";

const KIND_FILTERS = [
  { key: "all", label: "全部" },
  { key: "episode", label: "剧情介绍" },
  { key: "article", label: "资讯文章" },
] as const;

type KindFilter = (typeof KIND_FILTERS)[number]["key"];

export default function Search() {
  useSeo({
    title: "朋友的妈妈3内容搜索结果",
    description:
      "搜索《朋友的妈妈3》的剧情介绍与影视资讯文章，支持按内容类型筛选，快速找到你感兴趣的内容。",
    keywords: "朋友的妈妈3搜索,朋友的妈妈3查找,朋友的妈妈3剧情搜索",
  });

  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";

  const [input, setInput] = useState(initialQuery);
  const [keyword, setKeyword] = useState(initialQuery);
  const [kind, setKind] = useState<KindFilter>("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setKeyword(input.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [input]);

  useEffect(() => {
    if (!keyword) return;
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, [keyword, kind]);

  const results = useMemo(() => searchContent(keyword, kind), [keyword, kind]);
  const allCount = useMemo(() => searchContent(keyword, "all").length, [keyword]);
  const recommendations = getHotEpisodes(3);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setKeyword(input.trim());
    setLoading(true);
    setTimeout(() => setLoading(false), 300);
  };

  return (
    <>
      <section className="w-full bg-primary-950 px-4 md:px-6 py-10 md:py-14">
        <nav className="w-full text-sm text-background-100/60 mb-4" aria-label="面包屑">
          <ol className="flex items-center gap-2 flex-wrap">
            <li>
              <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
                首页
              </Link>
            </li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li className="text-background-50">搜索结果</li>
          </ol>
        </nav>
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
          内容搜索
        </h1>
        <p className="mt-3 text-sm text-background-100/70">
          搜索范围为《朋友的妈妈3》的剧情介绍与资讯文章（前端演示，数据为虚构内容）。
        </p>
        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="relative flex-1">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-background-100/50">
              <i className="ri-search-line text-lg"></i>
            </span>
            <input
              type="text"
              name="q"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入关键词，如「雨夜」「苏晚晴」「预告」"
              className="w-full pl-11 pr-4 py-3 rounded-md bg-background-50/10 border border-background-50/25 text-background-50 text-sm placeholder:text-background-100/50 focus:outline-none focus:border-accent-400 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="px-6 py-3 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50 min-h-[50vh]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <p className="text-sm text-foreground-600">
            {keyword ? (
              <>
                关键词 <span className="text-accent-600 font-medium">“{keyword}”</span> 共
                找到 <span className="text-primary-600 font-medium">{allCount}</span> 条结果
              </>
            ) : (
              "输入关键词开始搜索"
            )}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {KIND_FILTERS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setKind(item.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  kind === item.key
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <LoadingState text="正在搜索…" />
        ) : !keyword ? (
          <div className="rounded-lg bg-background-100 border border-background-200/80 p-6 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-fire-line text-accent-500"></i>
              热门剧情推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {recommendations.map((episode) => (
                <EpisodeCard key={episode.id} episode={episode} />
              ))}
            </div>
          </div>
        ) : results.length === 0 ? (
          <div>
            <div className="rounded-lg bg-background-100 border border-background-200/80 p-10 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mx-auto mb-4">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <p className="text-base md:text-lg font-medium text-foreground-800">
                未找到相关内容
              </p>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词试试，或浏览下面的热门内容。
              </p>
            </div>
            <div className="mt-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500"></i>
                热门内容推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {recommendations.map((episode) => (
                  <EpisodeCard key={episode.id} episode={episode} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {results.map((item: SearchResult) => (
              <Link
                key={`${item.kind}-${item.id}`}
                to={item.link}
                className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-100 border border-background-200/80 hover:border-accent-300 transition-colors cursor-pointer"
              >
                <span className="w-full sm:w-40 h-40 sm:h-28 rounded-md overflow-hidden shrink-0 bg-foreground-900">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={item.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs ${
                        item.kind === "episode"
                          ? "bg-accent-100 text-accent-900"
                          : "bg-secondary-100 text-secondary-900"
                      }`}
                    >
                      {item.badge}
                    </span>
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {formatDate(item.date)}
                    </span>
                  </span>
                  <span className="block mt-2 font-medium text-base text-foreground-950 group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </span>
                  <span className="block mt-1.5 text-sm text-foreground-500 line-clamp-2">
                    {item.summary}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}